/**
 * Kenbri Group Holdco Dashboard
 *
 * Executive portfolio view aggregating all operating companies across
 * Industrial, Food, Real Estate, and Business Development divisions.
 * Displays consolidated financials with DoD/WoW/MoM/QoQ/YoY comparisons.
 * Uses data from individual company data files for SLM training compatibility.
 */
import { useState } from 'react';
import { Card, Text, Group, Stack, Badge, Progress, SimpleGrid, Table, Tabs, Select } from '@mantine/core';
import {
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconBuilding,
  IconUsers,
  IconCash,
  IconChartBar,
  IconArrowUpRight,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import TopNav from '../components/TopNav';
import { PANAGO_METRICS, type TimePeriod } from '../data/panagoData';
import { PHANTOM_METRICS } from '../data/phantomData';
import { APEX_METRICS } from '../data/apexData';

// Operating company data derived from individual data files
// Using real Rooke brand colors from each OpCo's website
const OPCO_DATA = {
  panago: {
    name: 'Panago Pizza',
    path: '/panago',
    color: '#FF585D', // Panago coral/red from brand
    mantineColor: 'red',
    revenue: PANAGO_METRICS.totalRevenue,
    ebitda: {
      current: Math.round(PANAGO_METRICS.totalRevenue.current * 0.15), // 15% margin
      DoD: PANAGO_METRICS.totalRevenue.DoD - 0.4,
      WoW: PANAGO_METRICS.totalRevenue.WoW - 0.2,
      MoM: PANAGO_METRICS.totalRevenue.MoM - 0.6,
      QoQ: PANAGO_METRICS.totalRevenue.QoQ - 0.4,
      YoY: PANAGO_METRICS.totalRevenue.YoY - 0.7,
    },
    locations: PANAGO_METRICS.totalStores,
    employees: Math.round(PANAGO_METRICS.totalStores * 25),
    primeRatio: PANAGO_METRICS.primeRatio.current,
    sameStoreSales: PANAGO_METRICS.sameStoreSales.current,
  },
  phantom: {
    name: 'Phantom Screens',
    path: '/phantom-screens',
    color: '#1d8087', // Phantom teal from brand
    mantineColor: 'teal',
    revenue: PHANTOM_METRICS.totalRevenue,
    ebitda: PHANTOM_METRICS.ebitda,
    locations: 1,
    dealers: PHANTOM_METRICS.totalDealers,
    employees: 850,
    fillRate: PHANTOM_METRICS.fillRate.current,
    onTimeDelivery: PHANTOM_METRICS.otif.current,
  },
  apex: {
    name: 'Apex Food Source',
    path: '/apex-food',
    color: '#b58045', // Apex gold/tan from brand
    mantineColor: 'yellow',
    revenue: APEX_METRICS.totalRevenue,
    ebitda: APEX_METRICS.ebitda,
    locations: 1,
    employees: 285,
    capacityUtilization: APEX_METRICS.oee.current,
    firstPassYield: APEX_METRICS.firstPassRate.current,
    clients: APEX_METRICS.totalClients,
  },
};

// Aggregate calculations
const PORTFOLIO = {
  totalRevenue: OPCO_DATA.panago.revenue.current + OPCO_DATA.phantom.revenue.current + OPCO_DATA.apex.revenue.current,
  totalEbitda: OPCO_DATA.panago.ebitda.current + OPCO_DATA.phantom.ebitda.current + OPCO_DATA.apex.ebitda.current,
  totalEmployees: OPCO_DATA.panago.employees + OPCO_DATA.phantom.employees + OPCO_DATA.apex.employees,
  totalLocations: OPCO_DATA.panago.locations + OPCO_DATA.phantom.locations + OPCO_DATA.apex.locations + OPCO_DATA.phantom.dealers,
};

// Format currency
const formatCurrency = (value: number, compact = false): string => {
  if (compact) {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

// Format percentage change with icon
const formatChange = (value: number) => {
  const isPositive = value > 0;
  const isNeutral = value === 0;
  const color = isPositive ? 'green' : isNeutral ? 'gray' : 'red';
  const Icon = isPositive ? IconTrendingUp : isNeutral ? IconMinus : IconTrendingDown;

  return (
    <Group gap={4} justify="flex-end" wrap="nowrap">
      <Icon size={14} color={isPositive ? '#16a34a' : isNeutral ? '#6b7280' : '#dc2626'} />
      <Text size="sm" c={color} fw={500}>
        {isPositive ? '+' : ''}{value.toFixed(1)}%
      </Text>
    </Group>
  );
};

// Calculate weighted average change across OpCos
const getPortfolioChange = (field: 'revenue' | 'ebitda', period: TimePeriod): number => {
  const panago = OPCO_DATA.panago[field];
  const phantom = OPCO_DATA.phantom[field];
  const apex = OPCO_DATA.apex[field];

  const totalCurrent = panago.current + phantom.current + apex.current;

  return (
    (panago.current * panago[period] + phantom.current * phantom[period] + apex.current * apex[period]) / totalCurrent
  );
};

export default function HoldcoPage() {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('MoM');

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#f8f9fa',
      }}
    >
      <TopNav title="Kenbri Group" />

      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        <Stack gap="lg">
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Text size="xl" fw={700} c="dark" ta="center">
              Kenbri Group Portfolio
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Industrial | Food | Real Estate | Business Development & Capital Divisions
            </Text>
            <Text size="xs" c="dimmed" ta="center" mt="xs">
              Creating and operating viable businesses that build sustainable ways to improve the lives of those associated with them
            </Text>
          </div>
          <Group justify="flex-end" align="flex-start" mb="lg" gap="md">
            <Select
              value={selectedPeriod}
              onChange={(v) => setSelectedPeriod(v as TimePeriod)}
              data={[
                { value: 'DoD', label: 'Day over Day' },
                { value: 'WoW', label: 'Week over Week' },
                { value: 'MoM', label: 'Month over Month' },
                { value: 'QoQ', label: 'Quarter over Quarter' },
                { value: 'YoY', label: 'Year over Year' },
              ]}
              size="sm"
              w={180}
            />
            <Badge color="green" size="lg" variant="light">
              All Systems Operational
            </Badge>
          </Group>

          {/* Portfolio Summary Cards */}
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Portfolio Revenue</Text>
                <IconCash size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{formatCurrency(PORTFOLIO.totalRevenue, true)}</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(getPortfolioChange('revenue', selectedPeriod))}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Portfolio EBITDA</Text>
                <IconChartBar size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{formatCurrency(PORTFOLIO.totalEbitda, true)}</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(getPortfolioChange('ebitda', selectedPeriod))}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Total Employees</Text>
                <IconUsers size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{PORTFOLIO.totalEmployees.toLocaleString()}</Text>
              <Text size="xs" c="dimmed" mt="xs">Across all operating companies</Text>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Locations & Dealers</Text>
                <IconBuilding size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{PORTFOLIO.totalLocations.toLocaleString()}</Text>
              <Text size="xs" c="dimmed" mt="xs">Franchise, facilities & distribution</Text>
            </Card>
          </SimpleGrid>

          {/* Time Period Comparison */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md" ta="center">Portfolio Performance by Time Period</Text>
            <Tabs defaultValue="revenue">
              <Tabs.List mb="md">
                <Tabs.Tab value="revenue">Revenue Growth</Tabs.Tab>
                <Tabs.Tab value="ebitda">EBITDA Growth</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="revenue">
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Operating Company</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>DoD</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>WoW</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>MoM</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>QoQ</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>YoY</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {Object.entries(OPCO_DATA).map(([key, opco]) => (
                      <Table.Tr key={key}>
                        <Table.Td>
                          <Link to={opco.path} style={{ textDecoration: 'none' }}>
                            <Group gap="xs">
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: opco.color }} />
                              <Text fw={500} c="dark">{opco.name}</Text>
                              <IconArrowUpRight size={14} color="#6b7280" />
                            </Group>
                          </Link>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatChange(opco.revenue.DoD)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatChange(opco.revenue.WoW)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatChange(opco.revenue.MoM)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatChange(opco.revenue.QoQ)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatChange(opco.revenue.YoY)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text fw={500}>{formatCurrency(opco.revenue.current, true)}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    <Table.Tr style={{ background: '#f1f5f9' }}>
                      <Table.Td>
                        <Text fw={600}>Portfolio Total</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(getPortfolioChange('revenue', 'DoD'))}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(getPortfolioChange('revenue', 'WoW'))}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(getPortfolioChange('revenue', 'MoM'))}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(getPortfolioChange('revenue', 'QoQ'))}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(getPortfolioChange('revenue', 'YoY'))}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text fw={600}>{formatCurrency(PORTFOLIO.totalRevenue, true)}</Text>
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Tabs.Panel>

              <Tabs.Panel value="ebitda">
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Operating Company</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>DoD</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>WoW</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>MoM</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>QoQ</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>YoY</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>EBITDA</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {Object.entries(OPCO_DATA).map(([key, opco]) => (
                      <Table.Tr key={key}>
                        <Table.Td>
                          <Link to={opco.path} style={{ textDecoration: 'none' }}>
                            <Group gap="xs">
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: opco.color }} />
                              <Text fw={500} c="dark">{opco.name}</Text>
                              <IconArrowUpRight size={14} color="#6b7280" />
                            </Group>
                          </Link>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatChange(opco.ebitda.DoD)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatChange(opco.ebitda.WoW)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatChange(opco.ebitda.MoM)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatChange(opco.ebitda.QoQ)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatChange(opco.ebitda.YoY)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text fw={500}>{formatCurrency(opco.ebitda.current, true)}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    <Table.Tr style={{ background: '#f1f5f9' }}>
                      <Table.Td>
                        <Text fw={600}>Portfolio Total</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(getPortfolioChange('ebitda', 'DoD'))}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(getPortfolioChange('ebitda', 'WoW'))}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(getPortfolioChange('ebitda', 'MoM'))}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(getPortfolioChange('ebitda', 'QoQ'))}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(getPortfolioChange('ebitda', 'YoY'))}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text fw={600}>{formatCurrency(PORTFOLIO.totalEbitda, true)}</Text>
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Tabs.Panel>
            </Tabs>
          </Card>

          {/* Operating Company Cards */}
          <Text size="lg" fw={600} ta="center">Operating Companies</Text>
          <SimpleGrid cols={{ base: 1, md: 3 }}>
            {/* Panago */}
            <Card withBorder padding="lg" radius="md" component={Link} to="/panago" style={{ textDecoration: 'none', cursor: 'pointer', borderTop: '3px solid #FF585D' }}>
              <Group justify="space-between" mb="md">
                <Group gap="sm">
                  <div style={{ width: 40, height: 40, background: '#FF585D', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text size="lg" c="white" fw={700}>P</Text>
                  </div>
                  <div>
                    <Text fw={600} style={{ fontFamily: 'Poppins, sans-serif' }}>Panago Pizza</Text>
                    <Text size="xs" c="dimmed">Pizza Franchise</Text>
                  </div>
                </Group>
                <IconArrowUpRight size={20} color="#6b7280" />
              </Group>

              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Revenue</Text>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>{formatCurrency(OPCO_DATA.panago.revenue.current, true)}</Text>
                    {formatChange(OPCO_DATA.panago.revenue[selectedPeriod])}
                  </Group>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Locations</Text>
                  <Text size="sm" fw={500}>{OPCO_DATA.panago.locations}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Same-Store Sales</Text>
                  <Text size="sm" fw={500} c="green">+{OPCO_DATA.panago.sameStoreSales}%</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Prime Ratio</Text>
                  <Text size="sm" fw={500}>{OPCO_DATA.panago.primeRatio}%</Text>
                </Group>
              </Stack>

              <Progress value={OPCO_DATA.panago.revenue.current / PORTFOLIO.totalRevenue * 100} size="sm" color="#FF585D" mt="md" />
              <Text size="xs" c="dimmed" ta="center" mt={4}>
                {((OPCO_DATA.panago.revenue.current / PORTFOLIO.totalRevenue) * 100).toFixed(0)}% of portfolio
              </Text>
            </Card>

            {/* Phantom Screens */}
            <Card withBorder padding="lg" radius="md" component={Link} to="/phantom-screens" style={{ textDecoration: 'none', cursor: 'pointer', borderTop: '3px solid #1d8087' }}>
              <Group justify="space-between" mb="md">
                <Group gap="sm">
                  <div style={{ width: 40, height: 40, background: '#1d8087', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text size="lg" c="white" fw={700}>PS</Text>
                  </div>
                  <div>
                    <Text fw={600} style={{ fontFamily: 'Inter, sans-serif' }}>Phantom Screens</Text>
                    <Text size="xs" c="dimmed">Manufacturing & Distribution</Text>
                  </div>
                </Group>
                <IconArrowUpRight size={20} color="#6b7280" />
              </Group>

              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Revenue</Text>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>{formatCurrency(OPCO_DATA.phantom.revenue.current, true)}</Text>
                    {formatChange(OPCO_DATA.phantom.revenue[selectedPeriod])}
                  </Group>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Dealers</Text>
                  <Text size="sm" fw={500}>{OPCO_DATA.phantom.dealers}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Fill Rate</Text>
                  <Text size="sm" fw={500} c="green">{OPCO_DATA.phantom.fillRate}%</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">On-Time Delivery</Text>
                  <Text size="sm" fw={500}>{OPCO_DATA.phantom.onTimeDelivery}%</Text>
                </Group>
              </Stack>

              <Progress value={OPCO_DATA.phantom.revenue.current / PORTFOLIO.totalRevenue * 100} size="sm" color="#1d8087" mt="md" />
              <Text size="xs" c="dimmed" ta="center" mt={4}>
                {((OPCO_DATA.phantom.revenue.current / PORTFOLIO.totalRevenue) * 100).toFixed(0)}% of portfolio
              </Text>
            </Card>

            {/* Apex Food Source */}
            <Card withBorder padding="lg" radius="md" component={Link} to="/apex-food" style={{ textDecoration: 'none', cursor: 'pointer', borderTop: '3px solid #b58045' }}>
              <Group justify="space-between" mb="md">
                <Group gap="sm">
                  <div style={{ width: 40, height: 40, background: '#b58045', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text size="lg" c="white" fw={700}>AF</Text>
                  </div>
                  <div>
                    <Text fw={600} style={{ fontFamily: 'Inter, sans-serif' }}>Apex Food Source</Text>
                    <Text size="xs" c="dimmed">Contract Manufacturing</Text>
                  </div>
                </Group>
                <IconArrowUpRight size={20} color="#6b7280" />
              </Group>

              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Revenue</Text>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>{formatCurrency(OPCO_DATA.apex.revenue.current, true)}</Text>
                    {formatChange(OPCO_DATA.apex.revenue[selectedPeriod])}
                  </Group>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Active Clients</Text>
                  <Text size="sm" fw={500}>{OPCO_DATA.apex.clients}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">OEE</Text>
                  <Text size="sm" fw={500}>{OPCO_DATA.apex.capacityUtilization}%</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">First Pass Yield</Text>
                  <Text size="sm" fw={500} c="green">{OPCO_DATA.apex.firstPassYield}%</Text>
                </Group>
              </Stack>

              <Progress value={OPCO_DATA.apex.revenue.current / PORTFOLIO.totalRevenue * 100} size="sm" color="#b58045" mt="md" />
              <Text size="xs" c="dimmed" ta="center" mt={4}>
                {((OPCO_DATA.apex.revenue.current / PORTFOLIO.totalRevenue) * 100).toFixed(0)}% of portfolio
              </Text>
            </Card>
          </SimpleGrid>

          {/* EBITDA Margin Comparison */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md" ta="center">EBITDA Margin by Company</Text>
            <Stack gap="md">
              {Object.entries(OPCO_DATA).map(([key, opco]) => {
                const margin = (opco.ebitda.current / opco.revenue.current) * 100;
                return (
                  <div key={key}>
                    <Group justify="space-between" mb={4}>
                      <Group gap="xs">
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: opco.color }} />
                        <Text size="sm" fw={500}>{opco.name}</Text>
                      </Group>
                      <Text size="sm" fw={600}>{margin.toFixed(1)}%</Text>
                    </Group>
                    <Progress
                      value={margin}
                      size="lg"
                      color={opco.color}
                    />
                  </div>
                );
              })}
              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                <Group justify="space-between" mb={4}>
                  <Text size="sm" fw={600}>Portfolio Average</Text>
                  <Text size="sm" fw={600}>{((PORTFOLIO.totalEbitda / PORTFOLIO.totalRevenue) * 100).toFixed(1)}%</Text>
                </Group>
                <Progress
                  value={(PORTFOLIO.totalEbitda / PORTFOLIO.totalRevenue) * 100}
                  size="lg"
                  color="gray"
                />
              </div>
            </Stack>
          </Card>
        </Stack>
      </div>
    </div>
  );
}
